import type { ReactNode } from 'react'

import { ResponsiveBreadcrumbNav } from '../components/ResponsiveBreadcrumbNav'
import { ThemeSegment } from '../components/theme'
import type { BreadcrumbItem } from '../types/breadcrumb'

export type { BreadcrumbItem } from '../types/breadcrumb'

type Props = {
  title: string
  breadcrumbs?: BreadcrumbItem[]
  headerTrailing?: ReactNode
  headerBelow?: ReactNode
  adminLayout?: boolean
  children: ReactNode
  framed?: boolean
  /**
   * 未装框 + 管理布局时，主工作区是否参与外层纵向滚动。
   * 生图等双栏页应设为 false，由栏内各自 overflow，避免整页与右侧连成一条滚动条。
   */
  scrollableBody?: boolean
  /**
   * 管理布局外层（标题 + 主区整体）是否纵向可滚。
   * 视口内铺满的登录占位等设为 false，溢出由子节点 `scroll-region` 接管（保持主题滚动条）。
   */
  shellScrollable?: boolean
}

/**
 * 主内容区骨架：各页共用的顶栏（标题 → 面包屑 → 可选功能条）与下方工作区。
 */
export function ContentSkeleton({
  title,
  breadcrumbs,
  headerTrailing,
  headerBelow,
  adminLayout = false,
  children,
  framed = true,
  scrollableBody = true,
  shellScrollable = true,
}: Props) {
  const crumbs = breadcrumbs ?? []
  const headerBlock = (
    <header className="shell-rail sk-content-header min-h-[4.25rem] shrink-0 border-b border-[var(--shell-nav-border)] px-4 py-3 sm:min-h-[4.5rem] sm:px-6 sm:py-3.5 lg:px-8 xl:px-10">
      <div className="flex items-center justify-between gap-3 sm:gap-4">
        <div className="min-w-0 flex-1">
          <h1
            className="truncate font-display text-xl font-semibold leading-snug tracking-tight text-ink sm:text-2xl"
            title={title}
          >
            {title}
          </h1>
          <ResponsiveBreadcrumbNav items={crumbs} className="mt-1 min-h-[1.25rem]" />
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 sm:gap-3">
          {headerTrailing}
          <div className="hidden lg:block">
            <ThemeSegment compact nowrap />
          </div>
        </div>
      </div>
    </header>
  )

  const belowBlock =
    headerBelow != null ? (
      <div className="sk-header-below shrink-0 border-b border-[var(--shell-nav-border)] bg-panel/70 lg:bg-[var(--shell-content-bg)]">
        {headerBelow}
      </div>
    ) : null

  const framedBody = framed ? (
    <div className="sk-framed-outer box-border w-full max-w-full px-4 py-4 sm:px-6 sm:py-5 lg:px-8 lg:py-6 xl:px-10">
      <div className="sk-framed-inner rounded-[var(--radius)] border border-line bg-panel/80 p-4 shadow-card sm:p-6 lg:p-[max(1rem,calc(2rem*2/3))]">
        {children}
      </div>
    </div>
  ) : (
    children
  )

  if (adminLayout) {
    const bodyClass = framed
      ? 'sk-body-scroll scroll-region min-h-0 flex-1 bg-[var(--shell-content-bg)] lg:min-h-0 lg:overflow-y-auto'
      : scrollableBody
        ? 'sk-body-scroll scroll-region min-h-0 flex-1 overflow-y-auto bg-[var(--shell-content-bg)] lg:min-h-0'
        : 'flex min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden overflow-y-hidden bg-[var(--shell-content-bg)] lg:min-h-0'
    const shellFlex = 'flex min-h-0 flex-1 flex-col'
    const shellClass = shellScrollable
      ? `${shellFlex} scroll-region overflow-y-auto`
      : `${shellFlex} overflow-hidden`
    return (
      <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
        <div className={shellClass}>
          {headerBlock}
          {belowBlock}
          <div className={bodyClass}>{framedBody}</div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
      {headerBlock}
      {belowBlock}
      <div
        className={
          framed
            ? 'sk-body-scroll scroll-region min-h-0 flex-1 overflow-y-auto bg-[var(--shell-content-bg)]'
            : 'flex min-h-0 flex-1 flex-col overflow-hidden bg-[var(--shell-content-bg)]'
        }
      >
        {framed ? framedBody : children}
      </div>
    </div>
  )
}
